/**
 * useScrollToTop — world-class "tap status bar to scroll to top" for mobile.
 *
 * Handles both iOS and Android:
 * - **iOS**: Listens to Capacitor `statusTap` event (native status bar tap)
 * - **Both**: Pairs with `StatusBarTapZone` component that renders an
 *   invisible tap zone covering the status bar area.
 *
 * Smart scroll targets:
 * 1. AI Chat open → scroll chat to top
 * 2. Settings open (URL has `?setting=`) → scroll settings content to top
 * 3. Sidebar open → scroll sidebar to top
 * 4. Active task open (URL has `?tid=`) → scroll to the active task row
 *    (first tap), then to absolute top (second tap — "double-tap" pattern)
 * 5. Default → scroll main content to top (Today, Upcoming, etc.)
 *
 * Built-in `behavior: "smooth"` for native-feel animation.
 */

import { useEffect, useRef, useCallback } from "react"
import { Capacitor } from "@capacitor/core"
import { StatusBar } from "@capacitor/status-bar"
import type { PluginListenerHandle } from "@capacitor/core"
import { useMainScroll } from "@/app/providers/MainScrollProvider"
import { useViewContext } from "@/app/stores/viewContext"

// ---------------------------------------------------------------------------
// Core scroll logic
// ---------------------------------------------------------------------------

/**
 * Determines the correct scroll container and scrolls to top.
 *
 * Priority (checks only open/visible elements):
 * 1. AI Chat open → scroll chat drawer/panel
 * 2. Settings modal open → scroll settings content
 * 3. Sidebar drawer open → scroll sidebar
 * 4. Active task (?tid=) → scroll to task row or top
 * 5. Main content → scroll to top
 */
function performScrollToTop(mainScrollEl: HTMLElement | null) {
  const searchParams = new URLSearchParams(window.location.search)
  const state = useViewContext.getState()

  // 1. AI Chat open? (fullscreen on mobile, right panel on desktop)
  if (state.aiChatOpen) {
    const chatScrollEl = findScrollableElement("[data-ai-chat] .overflow-y-auto")
    if (chatScrollEl) {
      chatScrollEl.scrollTo({ top: 0, behavior: "smooth" })
      return
    }
  }

  // 2. Settings modal open? Check multiple selectors for robustness
  if (searchParams.has("setting")) {
    let settingsContent: HTMLElement | null = null

    // Try data-settings-content first (most specific)
    settingsContent = document.querySelector<HTMLElement>(
      "[data-settings-content]",
    )
    if (settingsContent && isScrollable(settingsContent)) {
      settingsContent.scrollTo({ top: 0, behavior: "smooth" })
      return
    }

    // Fallback: look for overflow-y-auto inside settings modal
    settingsContent = findScrollableElement("[data-settings-modal] .overflow-y-auto")
    if (settingsContent) {
      settingsContent.scrollTo({ top: 0, behavior: "smooth" })
      return
    }

    // Fallback: look for any visible dialog with overflow-y-auto
    settingsContent = findScrollableElement("[role='dialog'] .overflow-y-auto")
    if (settingsContent) {
      settingsContent.scrollTo({ top: 0, behavior: "smooth" })
      return
    }

    // Last resort: look for any scrollable overflow-y-auto in modal
    settingsContent = findScrollableElement(".overflow-y-auto")
    if (settingsContent) {
      settingsContent.scrollTo({ top: 0, behavior: "smooth" })
      return
    }
  }

  // 3. Sidebar drawer open? (mobile only, or desktop sidebar if scrollable)
  if (state.sidebarOpen) {
    const sidebarScroll = findScrollableElement("[data-sidebar-panel] .overflow-y-auto")
    if (sidebarScroll) {
      sidebarScroll.scrollTo({ top: 0, behavior: "smooth" })
      return
    }
  }

  // 4. Active task open? Double-tap pattern: first → task, second → top
  const activeTaskId = searchParams.get("tid")
  if (activeTaskId && mainScrollEl) {
    const taskRow = document.querySelector<HTMLElement>(
      `[data-task-id="${activeTaskId}"]`,
    )

    if (taskRow) {
      const containerRect = mainScrollEl.getBoundingClientRect()
      const taskRect = taskRow.getBoundingClientRect()
      const relativeTop =
        taskRect.top - containerRect.top + mainScrollEl.scrollTop

      // Respect CSS scroll-margin-top
      const style = window.getComputedStyle(taskRow)
      const scrollMarginTop = parseFloat(style.scrollMarginTop) || 0
      const targetOffset = relativeTop - scrollMarginTop

      // Already near the active task? Scroll to absolute top instead
      if (Math.abs(mainScrollEl.scrollTop - targetOffset) < 5) {
        mainScrollEl.scrollTo({ top: 0, behavior: "smooth" })
      } else {
        taskRow.scrollIntoView({ behavior: "smooth", block: "start" })
      }
      return
    }
  }

  // 5. Default: scroll main content to top
  // Covers Today, Upcoming, Inbox, Someday, Anyday, Logbook, Trash views.
  // Only runs when no other modals/drawers are open.
  if (mainScrollEl && isScrollable(mainScrollEl)) {
    mainScrollEl.scrollTo({ top: 0, behavior: "smooth" })
  }
}

/**
 * Check if an element is actually scrollable (has scroll height > client height)
 */
function isScrollable(el: HTMLElement): boolean {
  return el.scrollHeight > el.clientHeight + 1
}

/**
 * Helper to find the first visible scrollable element matching a selector
 */
function findScrollableElement(selector: string): HTMLElement | null {
  const els = document.querySelectorAll<HTMLElement>(selector)
  for (const el of els) {
    // Check if element is visible and scrollable
    const style = window.getComputedStyle(el)
    const isVisible = style.display !== "none" && style.visibility !== "hidden"
    if (isVisible && isScrollable(el)) {
      return el
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Mount once in MobileLayout. Sets up:
 * - Capacitor `statusTap` event listener (iOS native status bar tap)
 * - Pairs with `StatusBarTapZone` component (invisible tap overlay)
 * - Returns `scrollToTop` callback for the StatusBarTapZone
 */
export function useScrollToTop() {
  const { scrollRef } = useMainScroll()
  const listenerRef = useRef<PluginListenerHandle | null>(null)

  const scrollToTop = useCallback(() => {
    performScrollToTop(scrollRef.current)
  }, [scrollRef])

  // Register Capacitor statusTap listener (iOS only — native bridge)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    if (Capacitor.getPlatform() !== "ios") return

    let cancelled = false

    async function register() {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handle = await (StatusBar as any).addListener(
          "statusTap",
          () => {
            performScrollToTop(scrollRef.current)
          },
        )
        if (!cancelled) {
          listenerRef.current = handle
        } else {
          handle.remove()
        }
      } catch (e) {
        console.warn("[ScrollToTop] statusTap listener failed:", e)
      }
    }

    register()

    return () => {
      cancelled = true
      listenerRef.current?.remove()
      listenerRef.current = null
    }
  }, [scrollRef])

  // Also listen for the window-level statusTap event (Capacitor bridge compat)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    const handler = () => performScrollToTop(scrollRef.current)
    window.addEventListener("statusTap", handler)
    return () => window.removeEventListener("statusTap", handler)
  }, [scrollRef])

  return { scrollToTop }
}
